import { useAuthStore } from "@/stores/authStore";

async function request<T>(path: string, options: RequestInit = {}): Promise<{ data: T }> {
  const token = useAuthStore.getState().accessToken;

  const headers: Record<string, string> = {
    ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string>),
  };

  const makeRequest = (authHeader: Record<string, string>) =>
    fetch(`/api${path}`, { ...options, headers: { ...headers, ...authHeader } });

  let response = await makeRequest({});

  if (response.status === 401) {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) {
      useAuthStore.getState().logout();
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    const refreshResponse = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!refreshResponse.ok) {
      useAuthStore.getState().logout();
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    const refreshData = await refreshResponse.json();
    useAuthStore.getState().setTokens(refreshData.access_token, refreshData.refresh_token);
    response = await makeRequest({ Authorization: `Bearer ${refreshData.access_token}` });
  }

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return { data: undefined as T };
  }

  const data = (await response.json()) as T;
  return { data };
}

const client = {
  get: <T>(path: string, options?: { params?: Record<string, string> }) => {
    const url = options?.params ? `${path}?${new URLSearchParams(options.params)}` : path;
    return request<T>(url);
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T = void>(path: string, options?: { data?: unknown }) =>
    request<T>(path, {
      method: "DELETE",
      body: options?.data !== undefined ? JSON.stringify(options.data) : undefined,
    }),
};

export default client;
